import { Fragment, useEffect, useMemo } from 'react';

import { legalDocuments, type LegalBlock, type LegalDocument } from '../data/legalDocuments';
import { PageChrome } from './PageChrome';

type LegalPageProps = {
  document: LegalDocument;
};

type HeadingLink = {
  id: string;
  text: string;
};

const linkPattern = /(https?:\/\/[^\s;,)]*[\w/#-]|[\w.+-]+@[\w.-]+\.[\w.-]+)/g;
const withdrawalLabel = 'Elállás a szerződéstől';

function getHeadingLinks(blocks: LegalBlock[]) {
  return blocks
    .map((block, index) => {
      if (block.type !== 'heading' || block.level !== 2) {
        return null;
      }

      return {
        id: `legal-section-${index}`,
        text: block.text
      };
    })
    .filter((link): link is HeadingLink => link !== null);
}

function normalizeCells(row: string[], columnCount: number) {
  return Array.from({ length: columnCount }, (_, index) => row[index] ?? '');
}

function renderTextWithInternalLinks(text: string, keyPrefix: string) {
  if (!text.includes(withdrawalLabel)) {
    return <Fragment key={keyPrefix}>{text}</Fragment>;
  }

  const segments = text.split(withdrawalLabel);

  return segments.map((segment, index) => (
    <Fragment key={`${keyPrefix}-${index}`}>
      {segment}
      {index < segments.length - 1 ? (
        <a href="/elallas/" className="legal-inline-link">
          {withdrawalLabel}
        </a>
      ) : null}
    </Fragment>
  ));
}

function renderInlineText(text: string) {
  return text.split(linkPattern).map((part, index) => {
    if (!part) {
      return null;
    }

    const isUrl = part.startsWith('http://') || part.startsWith('https://');
    const isEmail = !isUrl && part.includes('@');

    if (!isUrl && !isEmail) {
      return renderTextWithInternalLinks(part, `${part}-${index}`);
    }

    return (
      <a key={`${part}-${index}`} href={isEmail ? `mailto:${part}` : part} className="legal-inline-link">
        {part}
      </a>
    );
  });
}

function renderMultilineText(text: string) {
  return text.split('\n').map((line, index) => (
    <Fragment key={`${line}-${index}`}>
      {index > 0 ? <br /> : null}
      {renderInlineText(line)}
    </Fragment>
  ));
}

function LegalTable({ rows }: { rows: string[][] }) {
  const [head, ...bodyRows] = rows;
  const columnCount = Math.max(...rows.map((row) => row.length));

  return (
    <div className="legal-table-wrap">
      <table className="legal-table">
        <thead>
          <tr>
            {normalizeCells(head, columnCount).map((cell, cellIndex) => (
              <th key={`${cell}-${cellIndex}`}>{renderMultilineText(cell)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {normalizeCells(row, columnCount).map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`}>{renderMultilineText(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LegalBlockView({ block, index }: { block: LegalBlock; index: number }) {
  if (block.type === 'heading') {
    const headingId = block.level === 2 ? `legal-section-${index}` : undefined;

    if (block.level === 2) {
      return <h2 id={headingId}>{block.text}</h2>;
    }

    return <h3>{block.text}</h3>;
  }

  if (block.type === 'table') {
    return <LegalTable rows={block.rows} />;
  }

  if (block.type === 'list') {
    return (
      <ul>
        {block.items.map((item, itemIndex) => (
          <li key={`${item}-${itemIndex}`}>{renderMultilineText(item)}</li>
        ))}
      </ul>
    );
  }

  return <p>{renderMultilineText(block.text)}</p>;
}

export function LegalPage({ document: legalDocument }: LegalPageProps) {
  const headingLinks = useMemo(() => getHeadingLinks(legalDocument.blocks), [legalDocument.blocks]);
  const siblingDocuments = legalDocuments.filter((document) => document.slug !== legalDocument.slug);

  useEffect(() => {
    const metaDescription = window.document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalTitle = window.document.title;
    const originalDescription = metaDescription?.getAttribute('content') ?? '';

    window.document.title = `${legalDocument.title} | Orienta`;
    metaDescription?.setAttribute('content', legalDocument.description);

    return () => {
      window.document.title = originalTitle;
      metaDescription?.setAttribute('content', originalDescription);
    };
  }, [legalDocument]);

  return (
    <PageChrome
      nav={siblingDocuments.map((document) => (
        <a key={document.slug} href={document.path}>
          {document.label}
        </a>
      ))}
    >
      <main className="legal-page-main">
        <section className="section-inner legal-hero">
          <p className="eyebrow">Jogi dokumentum</p>
          <h1>{legalDocument.title}</h1>
          <p>{legalDocument.description}</p>

          <dl className="legal-meta-grid">
            {legalDocument.meta.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{renderMultilineText(item.value)}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="section-inner legal-layout">
          <article className="legal-content">
            {legalDocument.blocks.map((block, index) => (
              <LegalBlockView key={`${block.type}-${index}`} block={block} index={index} />
            ))}
          </article>

          <aside className="legal-toc" aria-label="Tartalomjegyzék">
            <div>
              <p>Tartalom</p>
              <nav>
                {headingLinks.map((heading) => (
                  <a key={heading.id} href={`#${heading.id}`}>
                    {heading.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        </section>
      </main>
    </PageChrome>
  );
}
